import Base from '../base';
import UnderlyingSelect from '../underlying-select';
import ProductFiltersLinkedFields from './product-filters-linked-fields';
import dispatchCustomEvent from '../../utils/dispatch-custom-event';

/**
 * Needs a JSON object in the `data-option-filter-mapping` attribute in the format:
 * settings:
 * 	[
 * 		{
 * 			activeFilters: ['filter-leverage', 'filter-another']
 * 			productCategory: 'Booster'
 * 		}
 * ]
 */
class ProductFilters extends Base {
	constructor(el) {
		super(el);

		this.filterMapping = this.options.filterMapping.settings;

		this.filtersAll = this.queryAll('[data-filter]').map(filterEl => ({
			id: filterEl.getAttribute('data-filter'),
			el: filterEl,
		}));

		this.filtersLinkedFields = this.filtersAll
			.filter(filter => filter.el.hasAttribute('data-are-linked-fields'))
			.map(({ el: filterElement }) => new ProductFiltersLinkedFields(filterElement));

		this.underlyingSelect = new UnderlyingSelect({ el: this.refs.select });
		this.underlyingSelect.el.addEventListener('change', e => this.onUnderlyingSelected(e));

		this.on('change');
		this.on('submit');
	}

	get underlyingApiUrl() {
		const { productCategory } = this.refs;

		const cat = productCategory.value ?? null;

		const params = new URLSearchParams({
			cat,
			exchangesToExclude: this.options.exchangesToExclude,
			exchangesToInclude: this.options.exchangesToInclude,
		});

		return `${this.options.underlyingApiUrl}/tradeable?${params.toString()}`;
	}

	async start() {
		const {
			options: { underlyingApiUrl, preselectedUnderlying },
		} = this;

		if (!underlyingApiUrl) {
			console.warn('cannot create quick links without feedUrl');

			return;
		}

		this.isLoading = true;

		try {
			await this.underlyingSelect.init(this.underlyingApiUrl);

			if (preselectedUnderlying) {
				this.underlyingSelect.select(preselectedUnderlying);
				this.fetchProductCategories();
			}
		} catch (e) {
			console.warn('error while fetching:', e);
		} finally {
			this.isLoading = false;
		}

		this.dispatchFilterChange();

		this.toggleFilters(this.refs.productCategory.value);
	}

	onChange(e) {
		const { productCategory } = this.refs;
		const { target } = e;

		if (target === productCategory) {
			this.onProductCategorySelected(e);
			this.resetFilters();
			this.toggleFilters(productCategory.value);
		}

		this.dispatchFilterChange();
	}

	onSubmit() {
		this.filtersLinkedFields.forEach(ft => ft.destroyInputs());
	}

	onProductCategorySelected() {
		this.fetchUnderlyings();
	}

	onUnderlyingSelected(e) {
		const { target: { value } = {} } = e;

		if (value) {
			this.fetchProductCategories();
		} else {
			// selected "all"
			this.updateProductTypesFilter();
		}
	}

	async fetchProductCategories() {
		const underlyingKey = this.underlyingSelect.el.value;

		const {
			options: { underlyingApiUrl, exchangesToExclude, exchangesToInclude },
		} = this;

		const params = new URLSearchParams({
			exchangesToExclude,
			exchangesToInclude,
		});

		const productTypesApiUrl = `${underlyingApiUrl}/${underlyingKey}/categories?${params.toString()}`;

		try {
			const productTypes = await fetch(productTypesApiUrl).then(res => res.json());

			this.updateProductTypesFilter(productTypes);
		} catch (e) {
			console.warn('error while fetching:', e);
		} finally {
			this.isLoading = false;
		}
	}

	updateProductTypesFilter(productTypes = []) {
		const productTypeOptions = this.queryAll(
			'option',
			this.refs.productCategory,
		);

		// flatten the product (sub)categories for the underlying
		const underlyingProductTypes = productTypes.reduce(
			(result, productType) => [
				productType.categoryKey,
				...productType.subCategories.map(subCategory => subCategory.categoryKey),
				...result,
			],
			[],
		);

		if (underlyingProductTypes.length > 0) {
			productTypeOptions
				.filter(option => option.value !== '')
				.forEach(option => (option.disabled = !underlyingProductTypes.includes(option.value)));
		} else {
			productTypeOptions.forEach(option => (option.disabled = false));
		}
	}

	async fetchUnderlyings() {
		try {
			const { assetClasses } = await UnderlyingSelect.fetchApiResponse(this.underlyingApiUrl);

			this.underlyingSelect.update(assetClasses);
		} catch (e) {
			console.warn('error while fetching:', e);
		} finally {
			this.isLoading = false;
		}
	}

	toggleFilters(productCategory) {
		const productTypeFilters = this.getProductTypeFilters(productCategory);

		this.filtersAll.forEach((filter) => {
			const { id, el } = filter;
			const isShown = productTypeFilters.includes(id);

			el.classList.toggle('is-active', isShown);

			if (isShown) {
				el.setAttribute('data-is-visible', true);
			} else {
				el.removeAttribute('data-is-visible');
			}
		});

		this.toggleHeader(productTypeFilters);
	}

	getProductTypeFilters(productCategory = '') {
		const mapping = this.filterMapping
			.find(map => map.productCategory.toLowerCase() === productCategory.toLowerCase());

		return mapping ? mapping.activeFilters : [];
	}

	resetFilters() {
		this.filtersAll.forEach(this.resetFilter, this);
	}

	resetFilter(filter) {
		const inputs = this.queryAll('input, select, textarea', filter.el);

		inputs.forEach((input) => {
			const { type, tagName } = input;

			if (tagName === 'SELECT') {
				input.selectedIndex = 0;
			} else if (type === 'radio' || type === 'checkbox') {
				input.checked = false;
			} else {
				input.value = '';
			}
		});
	}

	toggleHeader(productFilters = []) {
		const hasNoSpecificFilters = productFilters.length === 0;

		this.refs.productSpecificHeader.classList.toggle(
			'is-hidden',
			hasNoSpecificFilters,
		);
	}

	dispatchFilterChange() {
		dispatchCustomEvent(document.body, 'filter:change', this.el);
	}
}

export default ProductFilters;
